import { ID_TO_GRAIN } from '../constants/dimensions';

export const getCm360Linking = (datasetRules, platform_grain_id) => {
    const linking = datasetRules?.find(
        rule => rule?.type === 'cm360Mapping' && rule?.config?.target_id === platform_grain_id,
    );
    if (linking) {
        const resolvedGrain = ID_TO_GRAIN[linking?.config?.source_level];
        linking.source_platform_grain = resolvedGrain;
        linking.source_information = {
            platform: linking.config?.source_platform,
            name: linking.config?.source_name,
            grain: linking.config?.source_level,
        };
    }

    return linking;
};

export const getAliases = (datasetRules, platform_grain_id) => {
    const alias = datasetRules?.find(
        rule =>
            rule?.config?.key === platform_grain_id &&
            rule?.type === 'alias' &&
            rule.config.field !== 'creative_size_id' &&
            !rule?.config?.default_field?.endsWith('_gwtax'),
    );
    return alias;
};

export const visibleCampaignAlias = (row, autoAlias) => {
    if (row?.alias?.config?.alias) {
        return row.alias.config.alias;
    }
    if (autoAlias !== false && row?.auto_alias) {
        return row.auto_alias;
    }
    return undefined;
};

export const visibleCampaignName = (row, autoAlias) =>
    visibleCampaignAlias(row, autoAlias) || row?.name;

export const getGwtaxAliases = (datasetRules, platform_grain_id) => {
    const alias = datasetRules?.find(
        rule =>
            rule?.config?.key === platform_grain_id &&
            rule?.type === 'alias' &&
            rule?.config?.default_field?.endsWith('_gwtax'),
    );
    return alias;
};

export const getSizeAliases = (datasetRules, platform_grain_id) => {
    const sizeAlias = datasetRules?.find(
        rule =>
            rule?.config?.key === platform_grain_id &&
            rule?.type === 'alias' &&
            rule.config.field === 'creative_size_id',
    );
    return sizeAlias;
};

export const getFilters = (datasetRules, platform_grain_id) => {
    const omit = datasetRules.some(
        rule => rule.type === 'grainFilter' && rule.config.key === platform_grain_id,
    );
    return omit;
};

export const getTags = (datasetRulesByType, platform_grain_id) => {
    const tags =
        datasetRulesByType?.tag
            ?.filter(
                rule =>
                    rule.type === 'tag' &&
                    rule.config.tag_type === 'alias' &&
                    rule.config.grain_id === platform_grain_id,
            )
            .reduce((t, tag) => {
                t[tag.config.bucket] = tag;
                return t;
            }, {}) || {};

    const assetsAsTags = datasetRulesByType?.image?.find(
        rule => ['image'].includes(rule.type) && rule.config.grain_id === platform_grain_id,
    );

    if (assetsAsTags && !tags.creative) {
        tags.creative = assetsAsTags;
    }

    return tags;
};

export const getActivityBuckets = (datasetRules, activity) => {
    const matchActivity =
        datasetRules?.filter(
            rule =>
                rule?.config?.key === activity.activityID &&
                rule?.type === 'tag' &&
                rule?.config?.tag_value === 'activities',
        ) || [];
    const matchAdditionalFilters = matchActivity.filter(rule => {
        if (!rule?.config?.additional_filters || rule?.config?.additional_filters.length === 0) {
            return true;
        }
        return rule?.config?.additional_filters.every(filter => {
            return filter.field === `${activity.grainType}_id` && filter.key === activity.grainID;
        });
    });

    return matchAdditionalFilters;
};

export const getActivityAliases = (datasetRules, activity) => {
    let matchActivity = datasetRules?.filter(
        rule =>
            rule?.type === 'alias' &&
            rule?.config?.key === activity.activityID &&
            rule?.config?.default_field === 'activity_name_alias',
    );
    if (matchActivity.length > 0 && matchActivity[0]?.config?.additional_filters?.length > 0) {
        const matchesAllAdditionalFilters = matchActivity.find(rule =>
            rule?.config?.additional_filters?.every(
                filter =>
                    filter.field === `${activity.grainType}_id` && filter.key === activity.grainID,
            ),
        );
        matchActivity = matchesAllAdditionalFilters;
        if (!matchesAllAdditionalFilters) {
            matchActivity = null;
        }
    } else {
        matchActivity = matchActivity[0];
    }
    return matchActivity;
};

export const getActivityFilters = (datasetRules, activity) => {
    const omit = datasetRules.some(
        rule =>
            rule.type === 'grainFilter' &&
            rule.config.key === activity.activityID &&
            rule.config.field === 'activity_id',
    );
    return omit;
};

export const applyDimensionRules = (datasetRulesByType, platform_grain_id) => {
    const result = {
        cm360_linking: getCm360Linking(datasetRulesByType?.cm360Mapping || [], platform_grain_id),
        alias: getAliases(datasetRulesByType?.alias || [], platform_grain_id),
        gwtaxAlias: getGwtaxAliases(datasetRulesByType?.alias || [], platform_grain_id),
        sizeAlias: getSizeAliases(datasetRulesByType?.alias || [], platform_grain_id),
        omit: getFilters(datasetRulesByType?.grainFilter || [], platform_grain_id),
        tags: getTags(datasetRulesByType, platform_grain_id),
    };
    return result;
};

export const applyActivityRules = (datasetRules, activity) => {
    const activityBuckets = getActivityBuckets(datasetRules, activity);
    const activityAlias = getActivityAliases(datasetRules, activity);
    const result = {
        omit: getActivityFilters(datasetRules, activity),
    };
    activityBuckets.forEach(bucket => (result[bucket.config.bucket] = bucket));

    if (activityAlias) {
        result.alias = activityAlias;
    }

    return result;
};

export const getInheritedRules = (path, datasetRulesByType) => {
    return path?.reduce(
        (ruleSet, ancestor) => {
            const tags = getTags(datasetRulesByType, ancestor);
            const alias = getAliases([...(datasetRulesByType?.alias || [])], ancestor);

            // path stores data most recent parent first, so first get parents, and only add grandparents
            // if there isn't a parent tag for that object.
            ruleSet.tags = Object.assign(tags, ruleSet.tags);

            if (alias && !ruleSet.alias) {
                ruleSet.alias = alias;
            }

            return ruleSet;
        },
        { tags: {} },
    );
};

const isCreativeUrlRule = rule =>
    rule.type === 'image' || (rule?.type === 'tag' && rule?.config?.bucket === 'creative');

const areBothCreatives = (ruleA, ruleB) => isCreativeUrlRule(ruleA) && isCreativeUrlRule(ruleB);

export const deleteCM360Rule = (updatedRules, source_id, target_id) => {
    const ruleIndex = updatedRules.findIndex(rule => {
        return rule?.config?.source_id === source_id && rule?.config?.target_id === target_id;
    });
    if (ruleIndex !== -1) {
        updatedRules.splice(ruleIndex, 1);
    }
};
export const checkSameRow = (rule, newRule) => {
    const sameBase = rule.config.key === newRule.config.key;
    let additionalFiltersMatch;
    // legacy datasets were created without additional_filters - only compare values if the existing rule contains an array (assume a match if undefined)
    if (
        rule.config.additional_filters &&
        (rule.config.additional_filters?.length > 0 ||
            newRule.config.additional_filters?.length > 0)
    ) {
        additionalFiltersMatch = rule.config.additional_filters?.every(filter =>
            newRule.config.additional_filters?.some(
                f => f.field === filter.field && f.key === filter.key,
            ),
        );
    } else {
        additionalFiltersMatch = true;
    }
    return sameBase && additionalFiltersMatch;
};
export const updateRules = (updatedRules, newRule, deleteRule) => {
    const ruleIndex = updatedRules.findIndex(rule => {
        const bothCreatives = areBothCreatives(rule, newRule);
        const isSameRow = checkSameRow(rule, newRule);
        const isSameType = rule.type === newRule.type;

        let isSameRule = true;
        if (newRule.type === 'cm360Mapping') {
            isSameRule = false;
        }
        if (newRule.type === 'tag') {
            isSameRule = newRule.config.bucket === rule.config.bucket;
        } else if (newRule.type === 'alias') {
            isSameRule =
                newRule.config.field === rule.config.field &&
                newRule.config.default_field === rule.config.default_field;
        }

        return isSameRow && ((isSameType && isSameRule) || bothCreatives);
    });
    if (ruleIndex === -1) {
        if (!deleteRule) {
            updatedRules.push(newRule);
        }
    } else if (deleteRule) {
        updatedRules.splice(ruleIndex, 1);
    } else {
        updatedRules[ruleIndex] = newRule;
    }
};

export const mapPricingGrain = (grains, datasetRules) =>
    grains?.map(grain => {
        const alias = getAliases(datasetRules, grain.platform_grain_id);

        if (alias) {
            return {
                ...grain,
                alias,
            };
        }

        return grain;
    }) || [];

export const groupDatasetRulesByType = datasetRules => {
    const datasetRulesByType = datasetRules.reduce((acc, rule) => {
        if (!acc[rule.type]) {
            acc[rule.type] = [];
        }
        acc[rule.type].push(rule);

        return acc;
    }, {});
    return datasetRulesByType;
};

/**
 * This function filters for only affected dimensions so that we don't process every single dimension in the hierarchy when there are only a few affected rows. This should improve performance on the UI side.
 *
 * @param {Array} allDimensions - An array containing all the dimensions to be processed.
 * @param {Object} affectedRows - An object representing the rows that are affected by the update.
 * @param {boolean} isFullUpdate - A flag indicating whether the update is a full update or not.
 * @param {Array} HIERARCHY_ORDER - An array defining the order of hierarchy for processing.
 * @returns {Object} - Returns an object with the with affected and unaffected dimensions.
 */
export const filterDimensions = (allDimensions, affectedRows, isFullUpdate, HIERARCHY_ORDER) => {
    const mostTopAffectedIndex = Math.min(
        ...Object.keys(affectedRows).map(key => HIERARCHY_ORDER.indexOf(key)),
    );

    let filteredDimensions;

    if (isFullUpdate) {
        filteredDimensions = { affected: {}, unaffected: allDimensions };
    } else {
        filteredDimensions = Object.keys(allDimensions)
            .filter(key => key !== 'platform')
            .sort((a, b) => {
                return HIERARCHY_ORDER.indexOf(a) - HIERARCHY_ORDER.indexOf(b);
            })
            .reduce(
                (acc, key) => {
                    const keyIndex = HIERARCHY_ORDER.indexOf(key);
                    const isAffected = keyIndex >= mostTopAffectedIndex;

                    acc.affected[key] = [];
                    acc.unaffected[key] = [];

                    const affectedRowIds = Object.values(affectedRows)[0];

                    if (isAffected) {
                        allDimensions[key]?.forEach(dimRow => {
                            const parentDimensions =
                                keyIndex > 0 ? HIERARCHY_ORDER.slice(0, keyIndex) : [];

                            // row is affected if it's in affected rows OR is a child/grandchild of affected row. Need to update inherited values.
                            const isDimRowAffected =
                                affectedRowIds?.includes(dimRow.id) ||
                                (Array.isArray(dimRow.path) &&
                                    dimRow.path.some(ancestorGrainId =>
                                        parentDimensions.some(parentDimension =>
                                            acc.affected[parentDimension]?.find(
                                                row => row?.grainID === ancestorGrainId,
                                            ),
                                        ),
                                    ));

                            if (isDimRowAffected) {
                                acc.affected[key].push(dimRow);
                            } else {
                                acc.unaffected[key].push(dimRow);
                            }
                        });
                    } else {
                        acc.unaffected[key] = allDimensions[key];
                    }

                    return acc;
                },
                { affected: {}, unaffected: {} },
            );
    }
    return filteredDimensions;
};
